import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog';
import {
  Plus,
  Save,
  ArrowLeft,
  Loader2,
  GripVertical,
  Trash2,
  Edit2,
  CheckCircle2,
  FileText,
  Settings2,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import {
  onboardingAPI,
  OnboardingDataset,
  DatasetSection,
  DatasetQuestion,
} from '../../lib/api';
import { getApiErrorMessage } from '../../lib/api/types/common';
import { useConfirm } from '../../contexts/ConfirmDialogContext';
import { toast } from 'sonner';
import SectionEditor from './SectionEditor';
import QuestionEditor from './QuestionEditor';

const DATASET_TYPES = [
  { value: 'onboarding', label: 'Supplier Onboarding' },
  { value: 'compliance', label: 'Compliance Assessment' },
  { value: 'risk_assessment', label: 'Risk Assessment' },
  { value: 'esg', label: 'ESG Assessment' },
  { value: 'financial', label: 'Financial Assessment' },
  { value: 'quality', label: 'Quality Assessment' },
  { value: 'security', label: 'Security Assessment' },
  { value: 'capability', label: 'Capability Assessment' },
  { value: 'periodic_review', label: 'Periodic Review' },
  { value: 'code_of_conduct', label: 'Code of Conduct' },
];

export default function DatasetBuilder() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const confirm = useConfirm();
  const isEditing = !!id;

  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [dataset, setDataset] = useState<Partial<OnboardingDataset>>({
    name: '',
    description: '',
    dataset_type: 'onboarding',
    version: '1.0',
    is_mandatory: true,
    is_active: true,
    allow_partial_save: true,
    pass_score: 0,
    auto_score: false,
    show_score_to_supplier: false,
    instructions: '',
  });
  const [sections, setSections] = useState<DatasetSection[]>([]);
  const [expandedSections, setExpandedSections] = useState<Set<number>>(new Set());

  // Section/Question editing state
  const [editingSectionId, setEditingSectionId] = useState<number | null>(null);
  const [sectionDialogOpen, setSectionDialogOpen] = useState(false);
  const [newSection, setNewSection] = useState<Partial<DatasetSection>>({
    title: '',
    description: '',
    sort_order: 0,
    is_required: true,
  });

  const [editingQuestionId, setEditingQuestionId] = useState<number | null>(null);
  const [questionDialogOpen, setQuestionDialogOpen] = useState(false);
  const [questionSectionId, setQuestionSectionId] = useState<number | null>(null);
  const [newQuestion, setNewQuestion] = useState<Partial<DatasetQuestion>>({
    question_text: '',
    help_text: '',
    question_type: 'text',
    is_required: true,
    sort_order: 0,
    is_scored: false,
    choices: [],
  });

  const [settingsOpen, setSettingsOpen] = useState(false);

  useEffect(() => {
    if (isEditing) {
      fetchDataset();
    }
  }, [id]);

  const fetchDataset = async () => {
    setLoading(true);
    try {
      const response = await onboardingAPI.getDataset(Number(id));
      const data = response.data;
      setDataset(data);

      // Fetch sections with questions
      const sectionsRes = await onboardingAPI.getSections({ dataset: id });
      const sectionsData = sectionsRes.data.results || sectionsRes.data || [];

      // Fetch questions for each section
      const sectionsWithQuestions = await Promise.all(
        sectionsData.map(async (section: DatasetSection) => {
          const questionsRes = await onboardingAPI.getQuestions({ section: section.id });
          const questions = questionsRes.data.results || questionsRes.data || [];
          return { ...section, questions };
        })
      );

      setSections(sectionsWithQuestions.sort((a, b) => a.sort_order - b.sort_order));

      // Expand all sections by default
      setExpandedSections(new Set(sectionsWithQuestions.map((s: DatasetSection) => s.id)));
    } catch (error) {
      console.error('Failed to fetch dataset:', error);
      toast.error('Failed to load questionnaire template');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveDataset = async () => {
    if (!dataset.name?.trim()) {
      toast.error('Please enter a template name');
      return;
    }

    setSaving(true);
    try {
      let savedDataset: OnboardingDataset;
      if (isEditing) {
        // CR-4156: saving an edit creates a NEW version rather than mutating the
        // live one. Version first, then apply the edits to the copy — that way
        // the archived version keeps the content it was actually used with,
        // which is the point of keeping it. Doing it the other way round would
        // archive a row that already contained the new text.
        const versionResponse = await onboardingAPI.createDatasetVersion(Number(id));
        const newVersion = versionResponse.data.dataset;
        // Version and live/published state belong to the server here — echoing
        // the loaded copy's values back would silently re-activate a draft.
        const { is_active: _isActive, is_published: _isPublished, ...edits } = dataset;
        const response = await onboardingAPI.updateDataset(newVersion.id, {
          ...edits,
          version: newVersion.version,
        });
        savedDataset = response.data;
        toast.success(
          `Saved as version ${newVersion.version} — ${versionResponse.data.previous_version} archived`
        );
        if (newVersion.id !== Number(id)) {
          navigate(`/onboarding/datasets/${newVersion.id}`, { replace: true });
        }
      } else {
        const response = await onboardingAPI.createDataset({
          ...dataset,
          company: user?.company,
        });
        savedDataset = response.data;
        toast.success('Template created successfully');
        navigate(`/onboarding/datasets/${savedDataset.id}`);
      }
      setDataset(savedDataset);
    } catch (error: unknown) {
      console.error('Failed to save dataset:', error);
      toast.error(getApiErrorMessage(error, 'Failed to save template'));
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    if (!isEditing) return;

    try {
      await onboardingAPI.publishDataset(Number(id));
      toast.success('Template published successfully');
      fetchDataset();
    } catch (error: unknown) {
      console.error('Failed to publish dataset:', error);
      toast.error(getApiErrorMessage(error, 'Failed to publish template'));
    }
  };

  // Section management
  const handleAddSection = () => {
    setEditingSectionId(null);
    setNewSection({
      title: '',
      description: '',
      sort_order: sections.length,
      is_required: true,
    });
    setSectionDialogOpen(true);
  };

  const handleEditSection = (section: DatasetSection) => {
    setEditingSectionId(section.id);
    setNewSection({
      title: section.title,
      description: section.description,
      sort_order: section.sort_order,
      is_required: section.is_required,
    });
    setSectionDialogOpen(true);
  };

  const handleSaveSection = async () => {
    if (!newSection.title?.trim()) {
      toast.error('Please enter a section title');
      return;
    }

    if (!isEditing) {
      toast.error('Please save the template first');
      return;
    }

    try {
      if (editingSectionId) {
        await onboardingAPI.updateSection(editingSectionId, newSection);
        toast.success('Section updated');
      } else {
        await onboardingAPI.createSection({
          ...newSection,
          dataset: Number(id),
        });
        toast.success('Section added');
      }
      setSectionDialogOpen(false);
      fetchDataset();
    } catch (error: unknown) {
      console.error('Failed to save section:', error);
      toast.error(getApiErrorMessage(error, 'Failed to save section'));
    }
  };

  const handleDeleteSection = async (sectionId: number) => {
    const ok = await confirm({ title: 'Delete Section', description: 'Are you sure you want to delete this section and all its questions?', confirmLabel: 'Delete', variant: 'destructive' });
    if (!ok) {
      return;
    }

    try {
      await onboardingAPI.deleteSection(sectionId);
      toast.success('Section deleted');
      fetchDataset();
    } catch (error) {
      console.error('Failed to delete section:', error);
      toast.error('Failed to delete section');
    }
  };

  // Question management
  const handleAddQuestion = (sectionId: number) => {
    setEditingQuestionId(null);
    setQuestionSectionId(sectionId);
    const section = sections.find(s => s.id === sectionId);
    const questionCount = section?.questions?.length || 0;
    setNewQuestion({
      question_text: '',
      help_text: '',
      question_type: 'text',
      is_required: true,
      sort_order: questionCount,
      is_scored: false,
      choices: [],
    });
    setQuestionDialogOpen(true);
  };

  const handleEditQuestion = (question: DatasetQuestion, sectionId: number) => {
    setEditingQuestionId(question.id);
    setQuestionSectionId(sectionId);
    setNewQuestion({
      question_text: question.question_text,
      help_text: question.help_text,
      question_type: question.question_type,
      is_required: question.is_required,
      sort_order: question.sort_order,
      is_scored: question.is_scored,
      max_score: question.max_score,
      choices: question.choices || [],
      validation_rules: question.validation_rules,
      conditional_logic: question.conditional_logic,
    });
    setQuestionDialogOpen(true);
  };

  const handleSaveQuestion = async () => {
    if (!newQuestion.question_text?.trim()) {
      toast.error('Please enter a question');
      return;
    }

    try {
      if (editingQuestionId) {
        await onboardingAPI.updateQuestion(editingQuestionId, newQuestion);
        toast.success('Question updated');
      } else {
        await onboardingAPI.createQuestion({
          ...newQuestion,
          section: questionSectionId!,
        });
        toast.success('Question added');
      }
      setQuestionDialogOpen(false);
      fetchDataset();
    } catch (error: unknown) {
      console.error('Failed to save question:', error);
      toast.error(getApiErrorMessage(error, 'Failed to save question'));
    }
  };

  const handleDeleteQuestion = async (questionId: number) => {
    const ok = await confirm({ title: 'Delete Question', description: 'Are you sure you want to delete this question?', confirmLabel: 'Delete', variant: 'destructive' });
    if (!ok) {
      return;
    }

    try {
      await onboardingAPI.deleteQuestion(questionId);
      toast.success('Question deleted');
      fetchDataset();
    } catch (error) {
      console.error('Failed to delete question:', error);
      toast.error('Failed to delete question');
    }
  };

  const toggleSectionExpanded = (sectionId: number) => {
    setExpandedSections(prev => {
      const next = new Set(prev);
      if (next.has(sectionId)) {
        next.delete(sectionId);
      } else {
        next.add(sectionId);
      }
      return next;
    });
  };

  const getQuestionTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      text: 'Short Text',
      textarea: 'Long Text',
      number: 'Number',
      date: 'Date',
      yes_no: 'Yes/No',
      single_choice: 'Single Choice',
      multiple_choice: 'Multiple Choice',
      file_upload: 'File Upload',
      rating: 'Rating',
      matrix: 'Matrix',
      signature: 'Signature',
      email: 'Email',
      phone: 'Phone',
      url: 'URL',
      currency: 'Currency',
      percentage: 'Percentage',
    };
    return labels[type] || type;
  };

  if (loading) {
    return (
      <div className="p-6">
        <Card className="max-w-5xl mx-auto">
          <CardContent className="py-12 flex flex-col items-center">
            <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
            <p className="text-muted-foreground">Loading template...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="max-w-5xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate('/onboarding/datasets')}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold">
                {isEditing ? 'Edit Template' : 'Create New Template'}
              </h1>
              <p className="text-muted-foreground text-sm">
                Build your questionnaire template with sections and questions
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {isEditing && (
              <>
                <Button variant="outline" size="sm" onClick={() => setSettingsOpen(true)}>
                  <Settings2 className="h-4 w-4 mr-2" />
                  Settings
                </Button>
                {!dataset.is_published && (
                  <Button variant="outline" size="sm" onClick={handlePublish}>
                    <CheckCircle2 className="h-4 w-4 mr-2" />
                    Publish
                  </Button>
                )}
              </>
            )}
            <Button onClick={handleSaveDataset} disabled={saving}>
              {saving ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              Save
            </Button>
          </div>
        </div>

        {/* Template Info */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Template Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Template Name *</Label>
                <Input
                  id="name"
                  value={dataset.name}
                  onChange={(e) => setDataset({ ...dataset, name: e.target.value })}
                  placeholder="e.g., Supplier Onboarding Questionnaire"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="type">Template Type *</Label>
                <Select
                  value={dataset.dataset_type}
                  onValueChange={(value) => setDataset({ ...dataset, dataset_type: value as OnboardingDataset['dataset_type'] })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {DATASET_TYPES.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={dataset.description || ''}
                onChange={(e) => setDataset({ ...dataset, description: e.target.value })}
                placeholder="Brief description of this questionnaire..."
                rows={2}
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="version">Version</Label>
                {/* CR-4156: the version is owned by the server once a template
                    exists — saving an edit archives the current version and
                    increments. Leaving it typeable let a user skip or collide
                    with the generated sequence, which is what made the column
                    meaningless. Still editable on a brand-new template so the
                    first version can be set. */}
                <Input
                  id="version"
                  value={dataset.version}
                  onChange={(e) => setDataset({ ...dataset, version: e.target.value })}
                  placeholder="1.0"
                  readOnly={isEditing}
                  disabled={isEditing}
                  title={isEditing ? 'Set automatically when you save an edit' : undefined}
                />
              </div>
              <div className="flex items-center space-x-2 pt-8">
                <Switch
                  id="mandatory"
                  checked={dataset.is_mandatory}
                  onCheckedChange={(checked) => setDataset({ ...dataset, is_mandatory: checked })}
                />
                <Label htmlFor="mandatory">Mandatory</Label>
              </div>
              <div className="flex items-center space-x-2 pt-8">
                <Switch
                  id="active"
                  checked={dataset.is_active}
                  onCheckedChange={(checked) => setDataset({ ...dataset, is_active: checked })}
                />
                <Label htmlFor="active">Active</Label>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Sections & Questions */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-lg">Sections & Questions</CardTitle>
              <CardDescription>
                {sections.length} section{sections.length !== 1 ? 's' : ''},{' '}
                {sections.reduce((acc, s) => acc + (s.questions?.length || 0), 0)} question
                {sections.reduce((acc, s) => acc + (s.questions?.length || 0), 0) !== 1 ? 's' : ''}
              </CardDescription>
            </div>
            <Button onClick={handleAddSection} disabled={!isEditing}>
              <Plus className="h-4 w-4 mr-2" />
              Add Section
            </Button>
          </CardHeader>
          <CardContent>
            {!isEditing && (
              <div className="text-center py-8 border rounded-lg border-dashed">
                <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">
                  Save the template first to add sections and questions
                </p>
              </div>
            )}

            {isEditing && sections.length === 0 && (
              <div className="text-center py-8 border rounded-lg border-dashed">
                <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="font-medium mb-2">No Sections Yet</h3>
                <p className="text-muted-foreground mb-4">
                  Start by adding a section to organize your questions
                </p>
                <Button onClick={handleAddSection}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add First Section
                </Button>
              </div>
            )}

            {sections.length > 0 && (
              <div className="space-y-4">
                {sections.map((section) => (
                  <div
                    key={section.id}
                    className="border rounded-lg overflow-hidden"
                  >
                    {/* Section Header */}
                    <div
                      className="flex items-center justify-between p-4 bg-muted/50 cursor-pointer"
                      onClick={() => toggleSectionExpanded(section.id)}
                    >
                      <div className="flex items-center gap-3">
                        <GripVertical className="h-4 w-4 text-muted-foreground" />
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{section.title}</span>
                            {section.is_required && (
                              <Badge variant="secondary" className="text-xs">Required</Badge>
                            )}
                          </div>
                          {section.description && (
                            <p className="text-sm text-muted-foreground">{section.description}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline">
                          {section.questions?.length || 0} question{(section.questions?.length || 0) !== 1 ? 's' : ''}
                        </Badge>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditSection(section);
                          }}
                        >
                          <Edit2 className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteSection(section.id);
                          }}
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                        {expandedSections.has(section.id) ? (
                          <ChevronUp className="h-4 w-4" />
                        ) : (
                          <ChevronDown className="h-4 w-4" />
                        )}
                      </div>
                    </div>

                    {/* Questions */}
                    {expandedSections.has(section.id) && (
                      <div className="p-4 space-y-3">
                        {section.questions?.map((question) => (
                          <div
                            key={question.id}
                            className="flex items-center justify-between p-3 bg-background border rounded-lg"
                          >
                            <div className="flex items-center gap-3 flex-1 min-w-0">
                              <GripVertical className="h-4 w-4 text-muted-foreground shrink-0" />
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="font-medium truncate">
                                    {question.question_text}
                                  </span>
                                  {question.is_required && (
                                    <span className="text-red-500">*</span>
                                  )}
                                </div>
                                <div className="flex items-center gap-2 mt-1">
                                  <Badge variant="outline" className="text-xs">
                                    {getQuestionTypeLabel(question.question_type)}
                                  </Badge>
                                  {question.is_scored && (
                                    <Badge variant="secondary" className="text-xs">
                                      Scored ({question.max_score} pts)
                                    </Badge>
                                  )}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-1 shrink-0">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleEditQuestion(question, section.id)}
                              >
                                <Edit2 className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteQuestion(question.id)}
                              >
                                <Trash2 className="h-4 w-4 text-red-500" />
                              </Button>
                            </div>
                          </div>
                        ))}

                        <Button
                          variant="outline"
                          size="sm"
                          className="w-full"
                          onClick={() => handleAddQuestion(section.id)}
                        >
                          <Plus className="h-4 w-4 mr-2" />
                          Add Question
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Section Dialog */}
      <SectionEditor
        open={sectionDialogOpen}
        onOpenChange={setSectionDialogOpen}
        section={newSection}
        onChange={setNewSection}
        onSave={handleSaveSection}
        isEditing={!!editingSectionId}
      />

      {/* Question Dialog */}
      <QuestionEditor
        open={questionDialogOpen}
        onOpenChange={setQuestionDialogOpen}
        question={newQuestion}
        onChange={setNewQuestion}
        onSave={handleSaveQuestion}
        isEditing={!!editingQuestionId}
      />

      {/* Settings Dialog */}
      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Template Settings</DialogTitle>
            <DialogDescription>
              Configure scoring and other template options
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="flex items-center justify-between">
              <div>
                <Label>Auto Score</Label>
                <p className="text-sm text-muted-foreground">
                  Automatically calculate score based on answers
                </p>
              </div>
              <Switch
                checked={dataset.auto_score}
                onCheckedChange={(checked) => setDataset({ ...dataset, auto_score: checked })}
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <Label>Show Score to Supplier</Label>
                <p className="text-sm text-muted-foreground">
                  Display the score to suppliers after submission
                </p>
              </div>
              <Switch
                checked={dataset.show_score_to_supplier}
                onCheckedChange={(checked) => setDataset({ ...dataset, show_score_to_supplier: checked })}
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <Label>Allow Partial Save</Label>
                <p className="text-sm text-muted-foreground">
                  Allow suppliers to save progress without completing all questions
                </p>
              </div>
              <Switch
                checked={dataset.allow_partial_save}
                onCheckedChange={(checked) => setDataset({ ...dataset, allow_partial_save: checked })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="passScore">Pass Score (%)</Label>
              <Input
                id="passScore"
                type="number"
                min="0"
                max="100"
                value={dataset.pass_score || 0}
                onChange={(e) => setDataset({ ...dataset, pass_score: Number(e.target.value) })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="validityDays">Validity (days)</Label>
              <Input
                id="validityDays"
                type="number"
                min="0"
                value={dataset.validity_days || ''}
                onChange={(e) => setDataset({ ...dataset, validity_days: e.target.value ? Number(e.target.value) : undefined })}
                placeholder="Leave empty for no expiry"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="instructions">Instructions</Label>
              <Textarea
                id="instructions"
                value={dataset.instructions || ''}
                onChange={(e) => setDataset({ ...dataset, instructions: e.target.value })}
                placeholder="Instructions shown to suppliers before filling out the questionnaire..."
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => setSettingsOpen(false)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
